import {
  MultiSelectWithTag,
  Toggle,
  useModalPortalTarget,
} from "@risalabs_frontend_org/oasis-ui-kit";
import { useEffect, useId, useState, type ComponentProps } from "react";

type SelectionProps = ComponentProps<typeof MultiSelectWithTag>;

export interface AutomationProps extends Omit<
  SelectionProps,
  "options" | "className" | "open" | "onOpenChange"
> {
  options?: SelectionProps["options"];
  onChange?: SelectionProps["onChange"];
  title?: string;
  agentModeLabel?: string;
  selectionLabel?: string;
  toggleProps?: Partial<ComponentProps<typeof Toggle>>;
  className?: string;
  selectionClassName?: string;
}

/** Toggle plus client selector that retains choices while disabled. */
const Automation = ({
  options = [],
  title = "Automation",
  agentModeLabel = "Agent Mode (Agentic)",
  selectionLabel = "Enable Agentic For",
  toggleProps,
  className,
  selectionClassName,
  defaultValue = [],
  value,
  onChange,
  placeholder = "Select filters",
  hideFooter = true,
  disabled = false,
  targetRef,
  ...selectionProps
}: AutomationProps) => {
  const modalTargetRef = useModalPortalTarget();
  const toggleId = useId();
  const [enabled, setEnabled] = useState(toggleProps?.defaultValue ?? false);
  const [selectedValues, setSelectedValues] = useState(defaultValue);

  useEffect(
    () => setEnabled(toggleProps?.defaultValue ?? false),
    [toggleProps?.defaultValue],
  );

  return (
    <div
      className={["automation-container", className].filter(Boolean).join(" ")}
    >
      <div className="mb-2 text-x-tiny font-semibold uppercase leading-5 tracking-[0.0375rem] text-primaryGray-9">
        {title}
      </div>
      <fieldset
        disabled={disabled}
        className="mb-5 flex h-9 min-w-0 items-center justify-between rounded-md border border-primaryGray-15 px-3"
      >
        <label
          htmlFor={toggleProps?.id ?? toggleId}
          className="text-x-tiny font-semibold text-primaryGray-1"
        >
          {agentModeLabel}
        </label>
        <Toggle
          label=""
          {...toggleProps}
          id={toggleProps?.id ?? toggleId}
          defaultValue={enabled}
          onChange={(data) => {
            setEnabled(data.value);
            toggleProps?.onChange?.(data);
          }}
        />
      </fieldset>
      <div
        className={[
          "automation-selection",
          enabled ? "automation-selection--expanded" : "",
        ]
          .filter(Boolean)
          .join(" ")}
        aria-hidden={!enabled}
      >
        <fieldset
          className="automation-selection__content"
          disabled={!enabled || disabled}
        >
          <div className="mb-2 text-xs font-semibold text-primaryGray-1">
            {selectionLabel}
          </div>
          <MultiSelectWithTag
            {...selectionProps}
            key={enabled ? "enabled" : "disabled"}
            options={options}
            value={value ?? selectedValues}
            onChange={(selectedIds) => {
              setSelectedValues(selectedIds);
              onChange?.(selectedIds);
            }}
            placeholder={placeholder}
            hideFooter={hideFooter}
            targetRef={targetRef ?? modalTargetRef}
            disabled={!enabled || disabled}
            className={selectionClassName}
          />
        </fieldset>
      </div>
    </div>
  );
};

export default Automation;
